import React, {
  DependencyList,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

import { IWixEvents, useEnvironment, useWixSdk } from '@wix/yoshi-flow-editor';

import { SettingsEventDefinition } from 'AiAssistantWidget/settingsEvents';

import { OnHandlerEvent, OnHandlerReset } from '../../types';

export type EventHandler<Data> = (data: Data) => void;
export type ResetHandler = () => void;
// This dynamically adjusted as new components listen events.
const handlersPerEvent: Record<string, EventHandler<any>[]> = {};

export type EditorSettingsEvents = {
  onHandlerEvent: OnHandlerEvent;
  onHandlerReset: OnHandlerReset;
};

export const EditorSettingsEventsContext =
  React.createContext<EditorSettingsEvents>({
    onHandlerEvent: () => {},
    onHandlerReset: () => {},
  });

export const EditorSettingsEventsProvider: React.FC<{
  onHandlerEvent: OnHandlerEvent;
  onHandlerReset: OnHandlerReset;
}> = ({ onHandlerEvent, onHandlerReset, children }) => (
  <EditorSettingsEventsContext.Provider
    value={{ onHandlerEvent, onHandlerReset }}
  >
    {children}
  </EditorSettingsEventsContext.Provider>
);

export function useOnEditorSettingsEvent<Data>(
  { eventName: name }: SettingsEventDefinition<Data>,
  handler: EventHandler<Data>,
) {
  const { isEditor } = useEnvironment();
  const { onHandlerEvent } = useContext(EditorSettingsEventsContext);

  // Keys are never deleted as there is no option to unsubscribe after handler.on(***) ,
  // but the list will be empty if no one listens effectively making it do nothing
  useEffect(() => {
    // There is no point to subscribe to settings events in viewer
    if (!isEditor) {
      return;
    }

    if (!handlersPerEvent[name]) {
      handlersPerEvent[name] = [];
      onHandlerEvent(name, (v: unknown) => {
        // Here, the event data type is implicitly cast into `Data`.
        // Ideally, we should parse the incoming params from `unknown` to `Data`
        // but as we are the only users of this event hub it should be fine
        handlersPerEvent[name].forEach((h) => h(v));
      });
    }
    // `name` is handled in return
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditor, onHandlerEvent]);

  useEffect(() => {
    // There is no point to unsubscribe settings events in viewer
    if (!isEditor) {
      return;
    }

    handlersPerEvent[name].push(handler);
    return () => {
      const ind = handlersPerEvent[name].indexOf(handler);
      if (ind >= 0) {
        handlersPerEvent[name].splice(ind, 1);
      }
    };
  }, [handler, isEditor, name]);
}

const handlersOnReset = new Set<ResetHandler>();
export function useOnEditorSettingsReset(
  handler: ResetHandler,
  deps: DependencyList = [],
) {
  const { onHandlerReset } = useContext(EditorSettingsEventsContext);
  const { Wix } = useWixSdk();
  const { isEditorX: isStudio } = useEnvironment();
  const handlerRef = useRef(handler);

  // deps are intentionally forwarded by callers for reset callback updates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const callable = () => handlerRef.current();
    handlersOnReset.add(callable);

    return () => {
      handlersOnReset.delete(callable);
    };
  }, []);

  const fireAllHandlers = useCallback(() => {
    handlersOnReset.forEach((h) => h());
  }, []);

  useEffect(() => {
    let cleanupInStudio: (() => void) | undefined;

    /**
     * There is bug in Studio/Viewer that public data is not updated after settings close
     * so we need to reset the handlers manually, subscribing on 'EDITOR_EVENT/TPA_ON_SETTINGS_CLOSE_EVENT'
     */
    if (isStudio) {
      const studioCb = (event: { type?: string }) => {
        if (event?.type === 'TPA_ON_SETTINGS_CLOSE_EVENT') {
          fireAllHandlers();
        }
      };

      (Wix as any)?.addEventListener?.('EDITOR_EVENT' as IWixEvents, studioCb);
      cleanupInStudio = () =>
        (Wix as any)?.removeEventListener?.(
          'EDITOR_EVENT' as IWixEvents,
          studioCb,
        );
    } else {
      onHandlerReset?.(fireAllHandlers);
    }

    return () => {
      cleanupInStudio?.();
    };
  }, [onHandlerReset, isStudio, Wix, fireAllHandlers]);
}
